import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Spinner } from "@/components/common";
import { loginTo } from "@/components/landing/data";
import { sportImage } from "@/components/landing/data";
import { MapPin, Star, Clock, ArrowLeft, LogIn } from "lucide-react";

function GuestHeader() {
  return (
    <header className="glass sticky top-0 z-50 border-b border-border">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" className="flex items-center"><img src="/spora1-logo.webp" alt="SPORA1" className="h-9 w-auto sm:h-10" /></Link>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm font-semibold text-muted-foreground hover:text-foreground">Sign in</Link>
          <Link to="/register" className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:bg-[#FF8533]">Get started</Link>
        </div>
      </div>
    </header>
  );
}

export default function BrowseFacility() {
  const { id } = useParams();
  const [f, setF] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/public/facilities/${id}`).then((r) => setF(r.data)).catch(() => setErr(true)); }, [id]);

  if (err) return <div className="min-h-screen bg-background"><GuestHeader /><div className="mx-auto max-w-3xl px-4 py-20 text-center text-muted-foreground">Facility not found. <Link to="/browse?cat=facilities" className="text-primary hover:underline">Back to browse</Link></div></div>;
  if (!f) return <div className="min-h-screen bg-background"><GuestHeader /><div className="py-24"><Spinner /></div></div>;

  const bookTo = loginTo(`/app/facilities/${id}`);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <GuestHeader />
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <Link to="/browse?cat=facilities" data-testid="browse-back" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to facilities</Link>

        <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
          <div className="relative h-56 sm:h-72">
            <img src={f.image_url || sportImage(f.sport)} alt={f.name} className="h-full w-full object-cover" />
            {f.rating != null && <span className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-full bg-white/95 px-3 py-1 text-sm font-semibold text-foreground shadow"><Star className="h-3.5 w-3.5 fill-primary text-primary" /> {f.rating}</span>}
          </div>
          <div className="p-6 sm:p-8">
            <h1 className="font-display text-3xl font-bold tracking-tight">{f.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              {f.sport && <span className="rounded-full bg-primary/10 px-2.5 py-0.5 font-medium capitalize text-primary">{f.sport}</span>}
              {(f.city || f.state) && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {[f.city, f.state].filter(Boolean).join(", ")}</span>}
            </div>
            {f.description && <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{f.description}</p>}

            <h2 className="mt-8 font-display text-xl font-bold">Bookable units</h2>
            {(f.units || []).length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">No bookable slots published yet.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {f.units.map((u) => (
                  <div key={u.id} data-testid={`browse-unit-${u.id}`} className="flex flex-col gap-3 rounded-2xl border border-border bg-background p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-semibold">{u.name}</p>
                      <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        {u.sport && <span className="capitalize">{u.sport}</span>}
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {u.open_time}–{u.close_time}</span>
                        {u.amenities && <span>{u.amenities}</span>}
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-4 sm:justify-end">
                      <p className="text-sm text-muted-foreground"><span className="font-display text-xl font-bold text-foreground">₹{u.price_per_slot}</span>/{u.slot_minutes}min</p>
                      <Link to={bookTo} data-testid={`browse-book-${u.id}`} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-[#FF8533]"><LogIn className="h-4 w-4" /> Sign in to book</Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
